import moment from "moment";
import _ from "lodash";

/**
 * Formatting and grouping of an installed base event's attributes for the event
 * detail panel (IBM-1093).
 *
 * The panel is narrow, so attributes are split into three groups:
 * - `longText`: free text (e.g. the Semicon "Solution") rendered as full-width blocks
 * - `facts`: short values rendered as a label/value grid
 * - `secondary`: values that duplicate the panel header or are import bookkeeping,
 *   rendered collapsed under "Further details"
 *
 * A field-level diff (`changes`) is not a single value; it becomes a `changeSets`
 * entry with one before/after pair per changed field.
 *
 * Values of a shape the panel does not know are shown as raw JSON (never as
 * "[object Object]") and their aliases are listed in `unformatted`, so the panel
 * can report them.
 */

/** Values longer than this (or containing a line break) are rendered as a block. */
export const LONG_TEXT_THRESHOLD = 60;

/**
 * Aliases that repeat information the panel already shows (nameplate, asset location)
 * or that only matter for the data import.
 */
export const SECONDARY_EVENT_ALIASES = new Set([
    "installedBaseNodeTitle",
    "installedBaseNodePath",
    "serialNumber",
    "regionKey",
    "sourceRow",
    "sourceEventId",
]);

/**
 * Snapshot of the node at event time (fab market, chamber type, address id, …),
 * written by the Semicon import. Context, not the event itself.
 */
const SECONDARY_ALIAS_PREFIX = "installedBaseNode";

export interface EventDetailAttribute {
    id: string;
    alias: string;
    label: string;
    value: string;
}

/** An update event's diff, as label/value facts ("Before", "After") per changed field. */
export interface EventChangeSet {
    id: string;
    alias: string;
    label: string;
    facts: EventDetailAttribute[];
}

export interface GroupedEventAttributes {
    longText: EventDetailAttribute[];
    facts: EventDetailAttribute[];
    secondary: EventDetailAttribute[];
    changeSets: EventChangeSet[];
    /** Aliases whose value had an unknown shape and is shown as raw JSON. */
    unformatted: string[];
}

type Translate = (key: string) => string;
const identity: Translate = (key) => key;

export const formatAttributeKey = (key: string): string =>
    key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

const isScalar = (value: any): boolean => value === null || typeof value !== "object";

const isMongoDate = (value: any): boolean => typeof value === "object" && !!value?.$date?.$numberLong;

/** True for values `formatEventValue` can only show as raw JSON. */
const isRawJsonValue = (value: any): boolean =>
    !isScalar(value) && !isMongoDate(value) && !(Array.isArray(value) && value.every(isScalar));

export const formatEventValue = (value: any): string | null => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (isMongoDate(value)) {
        return moment(Number(value.$date.$numberLong)).format("L LT");
    }
    if (Array.isArray(value) && value.every(isScalar)) {
        return value.filter((v) => v !== null && v !== undefined && v !== "").join(", ");
    }
    if (isRawJsonValue(value)) return JSON.stringify(value);
    // Format ISO date / datetime strings (e.g. "2026-06-22" or
    // "2026-06-22T10:49:04+00:00"); strict parsing avoids touching plain text.
    if (typeof value === "string") {
        const iso = moment(value, moment.ISO_8601, true);
        if (iso.isValid()) {
            return value.includes("T") ? iso.format("L LT") : iso.format("L");
        }
    }
    return String(value);
};

// A node-path attribute (array of { key, nodeTypeKey }) is rendered as a
// breadcrumb like the Asset Location, instead of raw JSON.
const parseNodePath = (raw: any): any[] | null => {
    let arr = raw;
    if (typeof raw === "string") {
        try {
            arr = JSON.parse(raw);
        } catch {
            return null;
        }
    }
    if (Array.isArray(arr) && arr.length > 0 && arr.every((n) => n && typeof n === "object" && "key" in n)) {
        return arr;
    }
    return null;
};

export const formatNodePath = (nodes: any[]): string =>
    [...nodes].reverse().map((n: any) => _.startCase(n.key)).join(" / ");

const isNodePathAttribute = (attr: any): boolean =>
    !!(parseNodePath(attr?.value) ?? parseNodePath(attr?.displayValue));

/**
 * Field-level diff of an update event (the `changes` attribute of e.g.
 * ASSET_LOCATION_UPDATE): `{ pathNodes: { old: [...], new: [...] } }`.
 */
const isChangeSet = (value: any): boolean =>
    !!value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length > 0 &&
    Object.values(value).every((change: any) =>
        !!change && typeof change === "object" && !Array.isArray(change) && ("old" in change || "new" in change));

const CHANGE_FIELD_LABELS: Record<string, string> = {
    pathNodes: "Asset Location",
};

// Node lists in a change set are ordered root → leaf and carry titles, unlike the
// leaf → root key paths of the Semicon import, so they are not run through formatNodePath.
const isChangeNodeList = (value: any): boolean =>
    Array.isArray(value) && value.length > 0 &&
    value.every((n) => !!n && typeof n === "object" && ("title" in n || "key" in n));

const formatChangeSide = (value: any): string =>
    isChangeNodeList(value)
        ? value.map((n: any) => n.title || _.startCase(n.key)).join(" / ")
        : formatEventValue(value) ?? "–";

/** Before/after facts per changed field; the field name is only added when several fields changed. */
export const getEventChangeFacts = (changes: Record<string, any>, t: Translate = identity): EventDetailAttribute[] => {
    const fields = Object.entries(changes);
    return fields.flatMap(([field, change]) => {
        const prefix = fields.length > 1 ? `${t(CHANGE_FIELD_LABELS[field] ?? formatAttributeKey(field))} – ` : "";
        return [
            { id: `${field}.old`, alias: field, label: `${prefix}${t("Before")}`, value: formatChangeSide(change.old) },
            { id: `${field}.new`, alias: field, label: `${prefix}${t("After")}`, value: formatChangeSide(change.new) },
        ];
    });
};

const hasRawJsonValue = (attr: any): boolean => {
    if (isNodePathAttribute(attr)) return false;
    const value = attr?.displayValue ?? attr?.value;
    if (isChangeSet(value)) {
        return Object.values(value).some((change: any) =>
            [change.old, change.new].some((side) => !isChangeNodeList(side) && isRawJsonValue(side)));
    }
    return isRawJsonValue(value);
};

export const formatEventAttributeValue = (attr: any): string | null => {
    const nodePath = parseNodePath(attr?.value) ?? parseNodePath(attr?.displayValue);
    if (nodePath) return formatNodePath(nodePath);
    return formatEventValue(attr?.displayValue ?? attr?.value);
};

export const isLongText = (value: string): boolean =>
    value.includes("\n") || value.length > LONG_TEXT_THRESHOLD;

export const isSecondaryAlias = (alias: string): boolean =>
    SECONDARY_EVENT_ALIASES.has(alias) || alias.startsWith(SECONDARY_ALIAS_PREFIX);

/** Splits the event attributes into long text, short facts and secondary details; empty values are dropped. */
export const splitEventAttributes = (attributes: any[], t: Translate = identity): GroupedEventAttributes => {
    const grouped: GroupedEventAttributes = { longText: [], facts: [], secondary: [], changeSets: [], unformatted: [] };

    attributes.filter(Boolean).forEach((attr: any) => {
        const alias = attr?.property?.alias ?? "";
        const id = String(attr.id ?? alias);
        const label = attr?.property?.preferredName || formatAttributeKey(alias);
        const raw = attr?.displayValue ?? attr?.value;
        if (isChangeSet(raw)) {
            if (hasRawJsonValue(attr)) grouped.unformatted.push(alias);
            grouped.changeSets.push({ id, alias, label, facts: getEventChangeFacts(raw, t) });
            return;
        }
        const value = formatEventAttributeValue(attr);
        if (value === null || value === "") return;
        if (hasRawJsonValue(attr)) grouped.unformatted.push(alias);
        const entry: EventDetailAttribute = { id, alias, label, value };
        if (isSecondaryAlias(alias)) {
            grouped.secondary.push(entry);
        } else if (!isNodePathAttribute(attr) && isLongText(value)) {
            grouped.longText.push(entry);
        } else {
            grouped.facts.push(entry);
        }
    });

    return grouped;
};
